import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import LoadingButton from "../components/LoadingButton";
import api from "../services/api";
import { motion, AnimatePresence } from "framer-motion";
import { FiLock, FiSmartphone, FiArrowLeft, FiCheckCircle, FiAlertCircle } from "react-icons/fi";

export default function VerifyPhone() {
  const { verifyCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const { user_id, phone, email } = location.state || {};
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    if (!user_id) navigate("/login", { replace: true });
  }, [user_id, navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setResendSuccess(false);

    if (!verificationCode || verificationCode.length !== 6) {
      setError(t("signup.error_code_invalid"));
      return;
    }

    setLoading(true);
    try {
      const result = await verifyCode({ user_id, code: verificationCode });
      if (result.success) {
        navigate(result.user?.onboarding_completed ? "/" : "/onboarding");
      } else {
        setError(result.message || t("signup.error_code_wrong"));
      }
    } catch (err) {
      setError(err.response?.data?.message || t("signup.error_generic"));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError("");
    setResendSuccess(false);
    try {
      const result = await api.signup({ user_id, resend: true });
      if (result.success) setResendSuccess(true);
      else setError(result.message || t("signup.error_resend"));
    } catch (err) {
      setError(t("signup.error_resend"));
    } finally {
      setLoading(false);
    }
  };

  const maskedPhone = phone ? `${"*".repeat(Math.max(0, phone.length - 4))}${phone.slice(-4)}` : "";

  if (!user_id) return null;

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gray-50 p-4">
      {/* Dynamic Background */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] right-[-10%] w-[60%] h-[60%] bg-emerald-200/20 rounded-full blur-[100px]" />
        <div className="absolute bottom-[-20%] left-[-10%] w-[60%] h-[60%] bg-blue-200/20 rounded-full blur-[100px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="bg-white/80 backdrop-blur-xl border border-white/60 p-8 sm:p-10 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)]">

          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-50 to-emerald-50 text-primary-600 mb-6 shadow-inner border border-white"
            >
              <FiLock className="w-8 h-8" />
            </motion.div>
            <h1 className="text-2xl font-extrabold text-gray-900 mb-2">
              {t("verify_phone.title", "Verify Email")}
            </h1>
            <p className="text-gray-500 font-medium">
              {t("verify_phone.subtitle", "Account verification required")}
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, ease: "easeInOut" }}
            className="bg-primary-50/50 border border-primary-100 rounded-xl p-4 mb-8 flex items-start gap-4"
          >
            <div className="bg-white p-2 rounded-lg shadow-sm text-primary-600">
              <FiSmartphone className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-primary-900 font-semibold mb-0.5">
                {t("verify_phone.info_message", "Code sent to your email")}
              </p>
              <p className="text-lg font-bold text-gray-900 tracking-wide">{maskedPhone}</p>
            </div>
          </motion.div>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6"
              >
                <div className="p-3 bg-red-50 text-red-600 border border-red-100 rounded-lg text-sm font-medium flex items-center gap-2">
                  <FiAlertCircle className="w-4 h-4" /> {error}
                </div>
              </motion.div>
            )}
            {resendSuccess && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6"
              >
                <div className="p-3 bg-green-50 text-green-600 border border-green-100 rounded-lg text-sm font-medium flex items-center gap-2">
                  <FiCheckCircle className="w-4 h-4" />
                  {t("signup.code_resent", "Code resent successfully")}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleVerify} className="space-y-6">
            <div>
              <input
                type="text"
                className="w-full border-2 border-primary-100 bg-white p-4 rounded-xl text-center text-4xl tracking-[0.5em] font-mono focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 transition-all text-primary-900 placeholder-primary-200/50"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                autoFocus
              />
              <p className="text-center text-xs text-gray-400 font-medium mt-3 tracking-wide uppercase">
                {t("signup.verification_code", "Enter 6-digit code")}
              </p>
            </div>

            <LoadingButton
              type="submit"
              loading={loading}
              className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white py-4 rounded-xl font-bold shadow-lg shadow-primary-600/20 hover:shadow-xl transition-all transform hover:-translate-y-0.5"
              disabled={verificationCode.length !== 6}
            >
              {t("signup.verify_button", "Verify & Continue")}
            </LoadingButton>

            <div className="flex flex-col gap-3 pt-2">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={loading}
                className="text-primary-600 hover:text-primary-700 text-sm font-bold hover:underline transition-colors disabled:opacity-50"
              >
                {t("signup.resend_code", "Didn't receive it? Resend")}
              </button>

              <div className="border-t border-gray-100 my-2"></div>

              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 text-gray-500 hover:text-gray-800 text-sm font-medium transition-colors"
              >
                <FiArrowLeft className="w-4 h-4" />
                {t("verify_phone.back_to_login", "Back to Login")}
              </Link>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
